import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GenericTable } from "@shared/components/GenericTable";
import { EyeIcon, PenBox, Search } from "lucide-react";
import {
  useMeterReasonCodesList,
} from "../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";

import { Button } from "@shared/ui/button";

import ReasonCodesAddEditForm from "./ReasonCodesAddEditForm";
import { PAGE_SIZE } from "@shared/utils/constants";

const MeterReasonCodes = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAddEditOpen, setIsAddEditOpen] = useState(false);
  const [details, setDetails] = useState();
  const search = decodeURIComponent(searchParams.get("search") || "");
  const currentPage = Number(searchParams.get("page")) || 1;

  const { data: codesList, isLoading } = useMeterReasonCodesList(
    remoteUtilityId,
    currentPage,
    PAGE_SIZE,
    search
  );
  const handleFormClose = () => {
    setIsAddEditOpen(false);
    setDetails(undefined);
  };

  const mappedData = codesList?.results?.map((code) => ({
    ...code,
    name: code.meterReadingStatus.name,
  }));

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(searchTerm && { search: searchTerm }),
    };
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
  };

  const pagination = codesList
    ? {
        currentPage,
        totalPages: Math.ceil(codesList?.count / PAGE_SIZE),
        totalItems: codesList?.count,
        startItem: (currentPage - 1) * PAGE_SIZE + 1,
        endItem: Math.min(currentPage * PAGE_SIZE, codesList?.count),
        itemsPerPage: PAGE_SIZE,
      }
    : undefined;

  const columns: TableColumn<any>[] = [
    { key: "reason", header: "Reason" },
    { key: "name", header: "Reading Status" },
    { key: "reasonCode", header: "Reason Code" },
    { key: "createdDate", header: "Created Date" },
    { key: "createdUserRemoteName", header: "Created By" },
  ];

  const actions = [
    {
      label: "Edit",
      icon: <PenBox className="h-5 w-5 text-muted-foreground" />,
      onClick: (row: any) => {
        setDetails(row);
        setIsAddEditOpen(true);
      },
    },
  ];

  return (
    <div className="space-y-4">
      <Button
        className="px-6 float-right"
        onClick={() => {
          setIsAddEditOpen(true);
        }}
      >
        Add
      </Button>
      <GenericTable
        data={mappedData}
        columns={columns}
        loading={isLoading}
        rowKey="name"
        onSearch={handleSearch}
        onSearchValueChange={handleSearch}
        actions={actions}
        actionsType="icons"
        searchPlaceholder="Search reason..."
        searchDebounceMs={500}
        emptyMessage="No status data found"
        emptyIcon={<Search className="h-8 w-8 text-muted-foreground mb-2" />}
        pagination={pagination}
        onPageChange={handlePageChange}
      />
      <ReasonCodesAddEditForm
        open={isAddEditOpen}
        onOpenChange={handleFormClose}
        initialData={details}
      />
    </div>
  );
};

export default MeterReasonCodes;
